import React, { FC, memo, useContext, useCallback } from 'react'

import { useMutation } from '@apollo/client'
import { pick } from 'lodash-es'
import { FormProvider, useForm } from 'react-hook-form'
import { useWizardStep } from 'react-wizard-primitive'

import { AppContext, SIGN_UP_CUSTOMER, LOGOUT_CUSTOMER } from 'Const'
import { SignUpStates, useCustomer, useSignUpState } from 'Hooks/checkout'
import useEvent from 'Hooks/useEvent'
import { Customer } from 'Types'
import { sendGTMAccountCreatedEvent } from 'Utils/analytics/gtmEvents'
import { noty } from 'Utils/index'

import { StepContext } from '..'
import NextStepBtn from '../NextStepBtn'

import Code from './Code'
import Details from './Details'
import LoggedCustomerView from './LoggedCustomerView'
import Password from './Password'
import Phone from './Phone'

export interface FormState {
  code?: string
  email: string
  firstName: string
  lastName: string
  password?: string
  phone: string
  subscribed?: boolean
}

const CustomerFieldsGroup: FC = () => {
  const { checkouting, nextStep } = useContext(StepContext)
  const { isActive, resetToStep } = useWizardStep()
  const customer = useCustomer()
  const [state, setState] = useSignUpState()
  const form = useForm<FormState>()

  const { handleSubmit, reset, getValues } = form

  const [signUp, { loading: signUpLoading }] = useMutation(SIGN_UP_CUSTOMER, {
    update(cache, { data }) {
      cache.modify({
        fields: {
          currentCustomer() {
            if (data.signUpCustomer.customer) {
              return {
                ...(data.signUpCustomer.customer as Customer),
                __typename: 'Customer',
              }
            }
          },
        },
      })
    },
    onCompleted: (data) => {
      if (data.signUpCustomer.state == SignUpStates.COMPLETED) {
        const customer = data.signUpCustomer.customer
        nextStep({ customer: pick(customer, ['firstName', 'lastName', 'email', 'phone']) })
        sendGTMAccountCreatedEvent(customer)
      } else if (data.signUpCustomer.state == SignUpStates.CODE) {
        noty('Enter code from SMS', 'success')
      }
      setState(data.signUpCustomer.state)
    },
  })
  const [logout] = useMutation(LOGOUT_CUSTOMER, {
    update(cache) {
      cache.modify({
        fields: {
          currentCustomer() {
            return null
          },
        },
      })
    },
    onCompleted: () => {
      resetToStep()
      setState(SignUpStates.PHONE)
      reset()
    },
  })

  const handleLogout = useCallback(() => logout(), [logout])

  const onSubmit = handleSubmit((input: FormState) => {
    if (customer?.signedUp) {
      nextStep({ customer: pick(customer, ['firstName', 'lastName', 'email', 'phone']) })
      return
    }
    void signUp({ variables: { input } })
  })

  const handleResend = useEvent(() => {
    void signUp({ variables: { input: { phone: getValues('phone') } } })
  })
  return (
    <FormProvider {...form}>
      <div className="checkout-step / mb-5">
        <h2 className="checkout-step__header / h4">
          <span>Contact</span>

          {isActive ? (
            state.isPhone && !customer?.signedUp ? null : (
              <button className="checkout-button / ml-auto" type="button" onClick={handleLogout}>
                Log out
              </button>
            )
          ) : (
            <button className="checkout-button / ml-auto" type="button" onClick={resetToStep}>
              Change
            </button>
          )}
        </h2>

        <form hidden={!isActive || customer?.signedUp}>
          <div className="row align-items-center">
            {customer?.signedUp ? null : <Phone disabled={!state.isPhone} />}
            {state.isCode ? (
              <Code handleResend={handleResend} />
            ) : state.isForm ? (
              <Details />
            ) : state.isPassword ? (
              <Password />
            ) : null}
          </div>
        </form>
        {customer?.signedUp ? (
          <LoggedCustomerView email={customer.email} firstName={customer.firstName} lastName={customer.lastName} />
        ) : null}
      </div>

      {isActive ? <NextStepBtn loading={signUpLoading || checkouting} onSubmit={onSubmit} /> : null}
    </FormProvider>
  )
}

export default memo(CustomerFieldsGroup)
